import React, { useState } from "react"
import { Modal, ModalBody, Spinner } from "reactstrap"
import { usePlanFeatures } from "../../hooks/usePlanFeatures"

const NAVY = "#221C5D"

// Denenebilirlik backend kataloğundan gelir (feature.trialable). Aşağıdaki liste
// yalnız GERİYE DÖNÜK yedektir: katalog alanını henüz döndürmeyen bir sürümle
// konuşulduğunda eski davranış korunsun diye durur.
const NOT_TRIALABLE_FALLBACK = []

const formatDate = (value) => {
  if (!value) return ""
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

/**
 * Paket dışı bir özelliğe tıklandığında açılan pencere.
 *
 * Önceki davranış menüyü kilitlemekti; kullanıcı neyi kaçırdığını görüyor ama
 * hiçbir şey yapamıyordu. Artık iki çıkış yolu sunulur: özelliği kısa süreliğine
 * ücretsiz denemek ya da paket yükseltme ekranına gitmek.
 *
 * Deneme hakkı özellik başına birdir; kullanılmışsa yalnız yükseltme seçeneği kalır.
 * Sunum + tek bir aksiyon çağrısı dışında iş kuralı BURADA DEĞİLDİR — hakkın olup
 * olmadığına backend karar verir, pencere yalnız onun döndürdüğü durumu çizer.
 */
const FeatureTrialModal = ({ isOpen, onClose, feature, trialable: trialableProp, trial, trialDays, onStartTrial, onUpgrade }) => {
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)
  const { canManagePlan } = usePlanFeatures()

  // Öncelik sırası: çağıranın verdiği katalog bilgisi → 403 gövdesinden gelen
  // bayrak → sabit yedek liste. İlk ikisi backend kaynaklıdır; yedek yalnız
  // katalog okunamadığında (ör. yönetici olmayan rol) devreye girer.
  const trialable = typeof trialableProp === "boolean"
    ? trialableProp
    : typeof feature?.trialable === "boolean"
      ? feature.trialable
      : !NOT_TRIALABLE_FALLBACK.includes(feature?.code)
  const used = !!trial
  const upgradeOnly = used || !trialable
  const label = feature?.label || "Bu özellik"

  const handleStart = async () => {
    setStarting(true)
    setError(null)
    try {
      await onStartTrial()
    } catch (e) {
      // 409 dâhil tüm ret sebepleri backend metniyle gösterilir; pencere açık
      // kalır ki kullanıcı yükseltme seçeneğine geçebilsin.
      setError(e?.message || "Deneme başlatılamadı")
    } finally {
      setStarting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalBody className="text-center px-4 py-4">
        <div className="mb-3">
          <i
            className={`bx ${used ? "bx-time-five" : trialable ? "bx-rocket" : "bx-lock-alt"}`}
            style={{ fontSize: 56, color: used ? "#E56A22" : NAVY }}
          />
        </div>

        <h5 className="fw-bold mb-2" style={{ color: "#333" }}>
          {label} paketinize dahil değil
        </h5>

        {!canManagePlan ? (
          <p className="text-muted mb-4" style={{ fontSize: 15 }}>
            Bu özelliği denemek ya da paketinizi yükseltmek için firma yöneticinize başvurun.
          </p>
        ) : !trialable ? (
          <p className="text-muted mb-4" style={{ fontSize: 15 }}>
            Bu özellik yalnızca paketinize dahil edildiğinde kullanılabilir.
            Kullanmak için paketinizi yükseltebilirsiniz.
          </p>
        ) : used ? (
          <p className="text-muted mb-4" style={{ fontSize: 15 }}>
            Bu özellik için {trialDays} günlük deneme hakkınızı
            {trial?.endsAt ? ` ${formatDate(trial.endsAt)} tarihinde` : ""} kullandınız.
            Kullanmaya devam etmek için paketinizi yükseltebilirsiniz.
          </p>
        ) : (
          <p className="text-muted mb-4" style={{ fontSize: 15 }}>
            Bu özelliği <strong>{trialDays} gün boyunca ücretsiz</strong> deneyebilir ya da
            özelliği kalıcı olarak kullanmak için paketinizi yükseltebilirsiniz.
          </p>
        )}

        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        {canManagePlan && !upgradeOnly && (
          <button
            className="btn btn-lg w-100 mb-2"
            style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999, fontWeight: 600 }}
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? (
              <>
                <Spinner size="sm" className="me-2" /> Başlatılıyor…
              </>
            ) : (
              <>
                <i className="bx bx-play-circle me-2" />
                {trialDays} Günlük Ücretsiz Denemeyi Başlat
              </>
            )}
          </button>
        )}

        {canManagePlan && (
          <button
            className={`btn btn-lg w-100 ${upgradeOnly ? "" : "btn-outline-secondary"}`}
            style={
              upgradeOnly
                ? { backgroundColor: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999, fontWeight: 600 }
                : { borderRadius: 999, fontWeight: 600 }
            }
            onClick={onUpgrade}
            disabled={starting}
          >
            <i className="bx bx-up-arrow-circle me-2" />
            Paketimi Yükselt
          </button>
        )}

        <button className="btn btn-link btn-sm text-muted mt-2" onClick={onClose} disabled={starting}>
          {canManagePlan ? "Vazgeç" : "Kapat"}
        </button>
      </ModalBody>
    </Modal>
  )
}

export default FeatureTrialModal
